import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoadingState } from "./components/LoadingState";
import { EntityFilters } from "./components/EntityFilters";
import { SearchInput } from "./components/SearchInput";
import { SearchFooter } from "./components/SearchFooter";
import { EmptyState } from "./components/EmptyState";
import { RecentSearches } from "./components/RecentSearches";
import { useGlobalSearch } from "./hooks/useGlobalSearch";
import { useRecentSearch } from "./hooks/useRecentSearch";
import { Close, Info } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  GlobalSearchResult,
  GlobalSearchEntityTypes,
  GlobalSearchFilterTypes,
  GlobalSearchStates,
  RecentSearchItem
} from "./types";
import {
  useDealsNavigation,
  getDealDetailedViewUrl
} from "@/services/hooks/entities/deal/useDealsNavigation";
import { SearchResults } from "./components/SearchResults";
import { partitionByNameMatch } from "./utils/partitionByNameMatch";
import Text from "@/components/ui/text";
import { Divider } from "@/components/ui/divider";
import IconContainer from "@/components/ui/icon-container";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useEnablePartnerships } from "@/pages/partners/hooks";

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const { enabled: showPartnerships } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const enablePartnerships = useEnablePartnerships();
  const partnershipsEnabled = showPartnerships && enablePartnerships;

  const enabledEntityTypes = useMemo(
    () =>
      partnershipsEnabled
        ? undefined
        : Object.values(GlobalSearchEntityTypes).filter(
            (entityType) =>
              entityType !== GlobalSearchEntityTypes.PARTNERS &&
              entityType !== GlobalSearchEntityTypes.PARTNERS_CONTACTS
          ),
    [partnershipsEnabled]
  );

  const hiddenFilters = useMemo(
    () =>
      partnershipsEnabled ? [] : [GlobalSearchFilterTypes.PARTNERS],
    [partnershipsEnabled]
  );

  const {
    query,
    setQuery,
    results,
    isLoading,
    activeFilter,
    setActiveFilter: handleFilterChange,
    selectedIndex,
    hasKeyboardNavigation,
    setSelectedIndex: updateSelectedIndex,
    clearSearch
  } = useGlobalSearch(enabledEntityTypes);

  useEffect(() => {
    if (hiddenFilters.includes(activeFilter)) {
      handleFilterChange(GlobalSearchFilterTypes.ALL);
    }
  }, [hiddenFilters, activeFilter, handleFilterChange]);

  const {
    recentSearches,
    isLoading: isLoadingRecentSearches,
    createRecentSearchFromResult
  } = useRecentSearch();

  const navigate = useNavigate();
  const { navigateToDetailedView: navigateToDealDetailedView } =
    useDealsNavigation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [recentSearchSelectedIndex, setRecentSearchSelectedIndex] =
    useState(-1);
  const [
    hasRecentSearchKeyboardNavigation,
    setHasRecentSearchKeyboardNavigation
  ] = useState(false);

  const { mainResults, otherMatches, orderedResults } = useMemo(() => {
    const { nameMatches, otherMatches: other } = partitionByNameMatch(results);
    return {
      mainResults: nameMatches,
      otherMatches: other,
      orderedResults: [...nameMatches, ...other]
    };
  }, [results]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const searchState = getSearchState();
      const isInRecentSearches = searchState === GlobalSearchStates.IDLE;

      const handleArrowNavigation = (direction: "up" | "down") => {
        e.preventDefault();
        if (isInRecentSearches) {
          const currentIndex = recentSearchSelectedIndex;
          const maxIndex = recentSearches.length - 1;
          const newIndex =
            direction === "down" ? currentIndex + 1 : currentIndex - 1;
          const boundedIndex =
            direction === "down"
              ? Math.min(newIndex, maxIndex)
              : Math.max(newIndex, -1);
          setRecentSearchSelectedIndex(boundedIndex);
          setHasRecentSearchKeyboardNavigation(true);
        } else {
          const newIndex =
            direction === "down" ? selectedIndex + 1 : selectedIndex - 1;
          updateSelectedIndex(newIndex, true);
        }
      };

      const handleEnterKey = () => {
        e.preventDefault();
        const openInNewTab = e.metaKey || e.ctrlKey;

        if (isInRecentSearches) {
          if (
            hasRecentSearchKeyboardNavigation &&
            recentSearchSelectedIndex >= 0 &&
            recentSearches[recentSearchSelectedIndex]
          ) {
            const selectedItem = recentSearches[recentSearchSelectedIndex];
            if (openInNewTab) {
              handleRecentSearchOpenInNewTab(selectedItem);
            } else {
              handleRecentSearchClick(selectedItem);
            }
          }
        } else if (
          hasKeyboardNavigation &&
          selectedIndex >= 0 &&
          orderedResults[selectedIndex]
        ) {
          const selectedResult = orderedResults[selectedIndex];
          if (openInNewTab) {
            handleResultOpenInNewTab(selectedResult);
          } else {
            handleResultClick(selectedResult);
          }
        }
      };

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case "ArrowDown":
          handleArrowNavigation("down");
          break;
        case "ArrowUp":
          handleArrowNavigation("up");
          break;
        case "Enter":
          handleEnterKey();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isOpen,
    results,
    selectedIndex,
    updateSelectedIndex,
    onClose,
    recentSearches,
    recentSearchSelectedIndex,
    hasRecentSearchKeyboardNavigation
  ]);

  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      clearSearch();
      updateSelectedIndex(-1);
      setRecentSearchSelectedIndex(-1);
      setHasRecentSearchKeyboardNavigation(false);
      setHasBeenOpened(true);
    } else if (!isOpen) {
      setHasBeenOpened(false);
    }
  }, [isOpen, hasBeenOpened, clearSearch, updateSelectedIndex]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const navigateToEntity = (
    entityType: GlobalSearchEntityTypes,
    entityId: string
  ) => {
    if (entityType === GlobalSearchEntityTypes.DEALS) {
      navigateToDealDetailedView(entityId);
    } else {
      navigate(`/${entityType}/${entityId}`);
    }
  };

  const getEntityUrl = (
    entityType: GlobalSearchEntityTypes,
    entityId: string
  ): string => {
    if (entityType === GlobalSearchEntityTypes.DEALS) {
      return getDealDetailedViewUrl(entityId);
    } else {
      return `/${entityType}/${entityId}`;
    }
  };

  const handleResultClick = (result: GlobalSearchResult) => {
    createRecentSearchFromResult(query, result);
    navigateToEntity(result.entityType, result.id);
    onClose();
  };

  const handleResultOpenInNewTab = (result: GlobalSearchResult) => {
    createRecentSearchFromResult(query, result);
    const url = getEntityUrl(result.entityType, result.id);
    if (url) {
      window.open(url, "_blank");
    }
    onClose();
  };

  const handleRecentSearchClick = (item: RecentSearchItem) => {
    if (item.resultId && item.resultEntityType) {
      navigateToEntity(item.resultEntityType, item.resultId);
    } else {
      setQuery(item.query);
    }
    onClose();
  };

  const handleRecentSearchOpenInNewTab = (item: RecentSearchItem) => {
    if (item.resultId && item.resultEntityType) {
      const url = getEntityUrl(item.resultEntityType, item.resultId);
      if (url) {
        window.open(url, "_blank");
      }
    }
    onClose();
  };

  const getSearchState = (): GlobalSearchStates => {
    if (!query.trim()) return GlobalSearchStates.IDLE;
    if (isLoading) return GlobalSearchStates.LOADING;
    if (results.length === 0) return GlobalSearchStates.EMPTY;
    return GlobalSearchStates.RESULTS;
  };

  const searchState = getSearchState();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="relative flex items-center"
      data-test="global-search-container"
    >
      {isOpen && (
        <div
          className="absolute top-0 left-1/2 transform -translate-x-1/2 mt-2 bg-surface-elevation1 rounded-[20px] shadow-popUp border border-border-border1 overflow-hidden z-50 w-[800px]"
          data-test="global-search-modal"
        >
          <div className="py-2 border-b border-border-border1 flex items-center">
            <div className="flex-1">
              <SearchInput
                value={query}
                onChange={setQuery}
                onClear={clearSearch}
                isLoading={isLoading}
              />
            </div>
            <div className="mt-2 mr-4">
              <IconButton
                onClick={onClose}
                variant="iconNoBg"
                size="sm"
                iconColor="secondary"
                data-test="global-search-close-button"
              >
                <Close />
              </IconButton>
            </div>
          </div>
          <div className="flex flex-col p-4 pb-0 h-[42vh]">
            {searchState !== GlobalSearchStates.IDLE && (
              <div className="p-2">
                <EntityFilters
                  activeFilter={activeFilter}
                  onFilterChange={handleFilterChange}
                  hiddenFilters={hiddenFilters}
                />
              </div>
            )}
            {searchState === GlobalSearchStates.IDLE && (
              <RecentSearches
                recentSearches={recentSearches}
                onRecentSearchClick={handleRecentSearchClick}
                onRecentSearchOpenInNewTab={handleRecentSearchOpenInNewTab}
                isLoading={isLoadingRecentSearches}
                selectedIndex={recentSearchSelectedIndex}
                hasKeyboardNavigation={hasRecentSearchKeyboardNavigation}
              />
            )}

            {searchState === GlobalSearchStates.LOADING && <LoadingState />}

            {searchState === GlobalSearchStates.EMPTY && (
              <EmptyState onClearSearch={clearSearch} />
            )}

            {searchState === GlobalSearchStates.RESULTS && (
              <>
                <Text variant="bodySecondary" className="px-3 py-2">
                  {t("globalSearch.searchStates.showingResults", {
                    count: results.length
                  })}
                </Text>
                <div className="flex-1 overflow-y-auto">
                  {mainResults.length > 0 && (
                    <SearchResults
                      results={mainResults}
                      selectedIndex={selectedIndex}
                      hasKeyboardNavigation={hasKeyboardNavigation}
                      onResultClick={handleResultClick}
                      onResultOpenInNewTab={handleResultOpenInNewTab}
                    />
                  )}
                  {mainResults.length === 0 && otherMatches.length > 0 && (
                    <div className="flex items-center gap-4 p-4">
                      <IconContainer size="md" color="tertiary">
                        <Info />
                      </IconContainer>
                      <Text variant="lgBodyTertiary">
                        {t("globalSearch.noNameMatch", { query })}
                      </Text>
                    </div>
                  )}
                  {otherMatches.length > 0 && (
                    <>
                      <Divider className="my-2" />
                      <Text variant="bodySecondary" className="px-3 py-2">
                        {t("globalSearch.otherMatches")}
                      </Text>
                      <SearchResults
                        results={otherMatches}
                        selectedIndex={selectedIndex - mainResults.length}
                        hasKeyboardNavigation={hasKeyboardNavigation}
                        onResultClick={handleResultClick}
                        onResultOpenInNewTab={handleResultOpenInNewTab}
                        isOtherMatch
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <SearchFooter />
        </div>
      )}
    </div>
  );
};
